import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { competitorAreas, ctaOptions, flows, improvementPriorities, resonanceOptions, selectionPriorities, usageLevels } from '../data/journeys';
import { needById, needs } from '../data/needs';
import { brand } from '../data/palette';
import type { Choice, NeedId, Route, SessionSignals, Stage } from '../types/filtek';
const emptySignals: SessionSignals = {
  exploredNeeds: [],
  exploredCases: [],
  contentExplored: []
};
export interface MemoryEntry {
  label: string;
  value: string;
}
interface ExperienceValue {
  stage: Stage;
  direction: number;
  signals: SessionSignals;
  route?: Route;
  stageIndex: number;
  stageCount: number;
  primaryNeed?: NeedId;
  accent: string;
  emphasis: string;
  memory: MemoryEntry[];
  start: (route: Route) => void;
  update: (patch: Partial<SessionSignals>) => void;
  push: (key: 'exploredNeeds' | 'exploredCases' | 'contentExplored', value: string) => void;
  next: () => void;
  back: () => void;
  goTo: (stage: Stage) => void;
  reset: () => void;
}
const ExperienceContext = createContext<ExperienceValue | null>(null);
const labelOf = (list: Choice[], id?: string) => list.find(item => item.id === id)?.label;
export function ExperienceProvider({
  children,
  initialRoute
}: {
  children: React.ReactNode;
  initialRoute?: Route;
}) {
  const [stage, setStage] = useState<Stage>(initialRoute ? flows[initialRoute][0] : 'entry');
  const [direction, setDirection] = useState(1);
  const [signals, setSignals] = useState<SessionSignals>(initialRoute ? {
    ...emptySignals,
    relationship: initialRoute
  } : emptySignals);
  const route = signals.relationship;
  const sequence = route ? flows[route] : [];
  const stageIndex = sequence.indexOf(stage);
  const update = useCallback((patch: Partial<SessionSignals>) => {
    setSignals(current => ({
      ...current,
      ...patch
    }));
  }, []);
  const push = useCallback((key: 'exploredNeeds' | 'exploredCases' | 'contentExplored', value: string) => {
    setSignals(current => {
      const list = current[key] as string[];
      if (list.includes(value)) return current;
      return {
        ...current,
        [key]: [...list, value]
      };
    });
  }, []);
  const start = useCallback((selected: Route) => {
    setDirection(1);
    setSignals(current => ({
      ...current,
      relationship: selected
    }));
    setStage(flows[selected][0]);
  }, []);
  const next = useCallback(() => {
    if (!route) return;
    const list = flows[route];
    const index = list.indexOf(stage);
    if (index < 0 || index === list.length - 1) return;
    setDirection(1);
    setStage(list[index + 1]);
  }, [route, stage]);
  const back = useCallback(() => {
    if (!route) return;
    const list = flows[route];
    const index = list.indexOf(stage);
    setDirection(-1);
    if (index <= 0) {
      setStage('entry');
      return;
    }
    setStage(list[index - 1]);
  }, [route, stage]);
  const goTo = useCallback((target: Stage) => {
    if (!route) {
      setStage(target);
      return;
    }
    const list = flows[route];
    setDirection(list.indexOf(target) >= list.indexOf(stage) ? 1 : -1);
    setStage(target);
  }, [route, stage]);
  const reset = useCallback(() => {
    setDirection(-1);
    setSignals(emptySignals);
    setStage('entry');
  }, []);
  const priorityChoice = useMemo(() => [...improvementPriorities, ...selectionPriorities].find(item => item.id === signals.priority), [signals.priority]);
  const competitorChoice = useMemo(() => competitorAreas.find(item => item.id === signals.competitorArea), [signals.competitorArea]);
  const primaryNeed = useMemo<NeedId | undefined>(() => signals.focusNeed ?? priorityChoice?.need ?? competitorChoice?.need ?? signals.exploredNeeds[0] as NeedId | undefined, [signals.focusNeed, signals.exploredNeeds, priorityChoice, competitorChoice]);
  const emphasis = priorityChoice?.emphasis ?? (primaryNeed ? needById(primaryNeed).label.toLowerCase() : 'fewer compromises');
  const accent = primaryNeed ? needById(primaryNeed).accent : brand.green;
  const memory = useMemo<MemoryEntry[]>(() => {
    const entries: MemoryEntry[] = [];
    if (signals.relationship) {
      entries.push({
        label: 'Relationship',
        value: signals.relationship === 'new' ? 'New to Filtek' : signals.relationship === 'better' ? 'Looking for something better' : 'Already using Filtek'
      });
    }
    if (priorityChoice) entries.push({
      label: 'Priority',
      value: priorityChoice.label
    });
    if (signals.resonance) {
      entries.push({
        label: 'Recognition',
        value: labelOf(resonanceOptions, signals.resonance) ?? ''
      });
    }
    if (signals.usage) {
      entries.push({
        label: 'Current use',
        value: labelOf(usageLevels, signals.usage) ?? ''
      });
    }
    if (competitorChoice) {
      entries.push({
        label: 'Other brand used for',
        value: competitorChoice.label
      });
    }
    if (signals.focusNeed) {
      entries.push({
        label: 'Focus area',
        value: needById(signals.focusNeed).label
      });
    }
    if (signals.exploredNeeds.length) {
      entries.push({
        label: 'Explored',
        value: signals.exploredNeeds.map(id => needs.find(need => need.id === id)?.label ?? '').join(', ')
      });
    }
    if (signals.exploredCases.length) {
      entries.push({
        label: 'Cases viewed',
        value: `${signals.exploredCases.length} situations`
      });
    }
    if (signals.contentExplored.length) {
      entries.push({
        label: 'Content opened',
        value: signals.contentExplored.join(', ')
      });
    }
    if (signals.cta) {
      entries.push({
        label: 'Next step',
        value: ctaOptions.find(item => item.id === signals.cta)?.label ?? ''
      });
    }
    return entries;
  }, [signals, priorityChoice, competitorChoice]);
  const value: ExperienceValue = {
    stage,
    direction,
    signals,
    route,
    stageIndex,
    stageCount: sequence.length,
    primaryNeed,
    accent,
    emphasis,
    memory,
    start,
    update,
    push,
    next,
    back,
    goTo,
    reset
  };
  return <ExperienceContext.Provider value={value} data-id="element-451" data-mp="3qf4bzip2e3i7">{children}</ExperienceContext.Provider>;
}
export function useExperience(): ExperienceValue {
  const context = useContext(ExperienceContext);
  if (!context) throw new Error('useExperience must be used inside ExperienceProvider');
  return context;
}